type Tag<T> = { item: T; y: number };

/** Tags that can't all sit at their own dots, so they get placed as one evenly-spaced run. */
type Cluster<T> = { tags: Tag<T>[]; wantedSum: number };

/**
 * Places value tags so none overlap, each as near its own dot as the rest allow.
 *
 * Claude wrote this. It's isotonic regression (pool adjacent violators) with a minimum gap, which
 * gives the placement that moves the tags least. Look that up rather than re-deriving it.
 *
 * Tags that would collide merge into a cluster and share the space around their average dot, so a
 * crowded group spreads either side of where those series sit rather than sliding down the panel.
 * Merging repeats: widening one cluster can push it into the one above.
 *
 * Null when the plot is too short to hold them all at minGapPx.
 */
export function stackTagOffsets<T>(
    tags: Tag<T>[],
    bounds: { minGapPx: number; topPx: number; bottomPx: number },
): Tag<T>[] | null {
    const { minGapPx, topPx, bottomPx } = bounds;
    if (tags.length === 0) return [];
    if (tags.length * minGapPx > bottomPx - topPx) return null;

    // A cluster sits minGapPx apart, centred on the mean of where its tags wanted to be.
    const centre = (cluster: Cluster<T>) => cluster.wantedSum / cluster.tags.length;
    const spread = (cluster: Cluster<T>) => ((cluster.tags.length - 1) * minGapPx) / 2;
    const firstY = (cluster: Cluster<T>) => centre(cluster) - spread(cluster);
    const lastY = (cluster: Cluster<T>) => centre(cluster) + spread(cluster);

    // Top down: absorbing a tag widens the cluster, which can then reach the one above it.
    const clusters: Cluster<T>[] = [];
    [...tags]
        .sort((a, b) => a.y - b.y)
        .forEach((tag) => {
            clusters.push({ tags: [tag], wantedSum: tag.y });
            while (clusters.length > 1) {
                const last = clusters[clusters.length - 1];
                const previous = clusters[clusters.length - 2];
                if (firstY(last) - lastY(previous) >= minGapPx) break;

                previous.tags.push(...last.tags);
                previous.wantedSum += last.wantedSum;
                clusters.pop();
            }
        });

    // Wanted positions become real ones here; everything below only clamps them.
    const placed = clusters.flatMap((cluster) => {
        const start = firstY(cluster);
        return cluster.tags.map((tag, index) => ({ ...tag, y: start + index * minGapPx }));
    });

    // Keep the set inside the plot. Sweeping down then up lands on positions that are both in the box
    // and still minGapPx apart — which is only possible because of the height check above.
    const half = minGapPx / 2;
    for (let i = 0; i < placed.length; i++) {
        const floor = i === 0 ? topPx + half : placed[i - 1].y + minGapPx;
        placed[i].y = Math.max(placed[i].y, floor);
    }
    for (let i = placed.length - 1; i >= 0; i--) {
        const ceiling = i === placed.length - 1 ? bottomPx - half : placed[i + 1].y - minGapPx;
        placed[i].y = Math.min(placed[i].y, ceiling);
    }

    return placed;
}
